/** @jsx jsx */
import React, { useState } from "react";
import Select from "react-select";
import { jsx, css } from "@emotion/core";
import Cleave from "cleave.js/react";
import { Field, getIn } from "formik";
import _get from "lodash/get";

import { colors, borders, timings } from "../../theme";
import FormField from "../FormField";
import Label from "../Label";
import ValidationError from "../ValidationError";
import { number } from "prop-types";

const style = css`
    flex: 1;
    border: ${borders.width} solid ${colors.neutral4};
    border-radius: ${borders.radius};
    padding: 10px;
    max-height: 36px;
    outline: none;
    caret-color: ${colors.primary5};
    transition: border ${timings.medium};
    &:focus {
        outline: 3px solid ${colors.primary0};
        outline-offset: 0px;
        border: ${borders.width} solid ${colors.primary8};
    }
    &:hover {
        border: ${borders.width} solid ${colors.primary8};
    }
}
`;

const errorStyle = css`
    flex: 1;
    border: ${borders.width} solid ${colors.error5};
    border-radius: ${borders.radius};
    padding: 10px;
    max-height: 36px;
    outline: none;
    background-color: ${colors.error1};
    caret-color: ${colors.error5};
    transition: border ${timings.medium};
    &:focus {
        outline: 3px solid ${colors.error0};
        outline-offset: 0px;
        border: ${borders.width} solid ${colors.error7};
    }
`;

export const Input = ({ ...props }) => (
    <input {...props} css={props.error ? errorStyle : style} />
);

export const FormikInput = ({ field, form: { touched, errors }, ...props }) => (
    <>
        <input
            type="text"
            {...field}
            {...props}
            css={touched[field.name] && errors[field.name] ? errorStyle : style}
        />
        {touched[field.name] && errors[field.name] && (
            <ValidationError
                data-testid={`${props["data-testid"]}-validation-message`}
                className="validation-error"
            >
                {errors[field.name]}
            </ValidationError>
        )}
    </>
);

export const BirthdateInput = ({ ...props }) => (
    <Cleave
        options={{
            date: true,
            delimiter: "/",
            datePattern: ["d", "m", "Y"],
        }}
        {...props}
        css={props.error ? errorStyle : style}
    />
);

export const FormikBirthdateInput = ({
    field,
    form: { touched, errors },
    ...props
}) => (
    <>
        <BirthdateInput
            type="text"
            {...field}
            {...props}
            css={touched[field.name] && errors[field.name] ? errorStyle : style}
        />
        {touched[field.name] && errors[field.name] && (
            <ValidationError
                data-testid={`${props["data-testid"]}-validation-message`}
                className="validation-error"
            >
                {errors[field.name]}
            </ValidationError>
        )}
    </>
);

const buttonStyle = css`
    position: absolute;
    right: 1px;
    top: 0;
    bottom: 0;
    text-transform: uppercase;
    color: ${colors.info6};
    font-size: 12px;
    border: none;
    margin: 1px;
    outline: none;
    font: inherit;
    background: transparent;
`;

export const PasswordInput = ({ ...props }) => {
    const [showPassword, setShowPassword] = useState(false);
    return (
        <div
            css={css`
                position: relative;
            `}
        >
            <Input
                css={css`
                    width: 100%;
                `}
                {...props}
                type={showPassword ? "" : "password"}
            />
            <button
                type="button"
                css={buttonStyle}
                onClick={() => setShowPassword(!showPassword)}
            >
                {showPassword ? "Hide" : "Show"}
            </button>
        </div>
    );
};

const customStyles = {
    container: (base, state) => ({
        ...base,
        borderRadius: `${borders.radius}`,
        outline: 0,
        maxHeight: 36,
        caretColor: `${colors.primary5}`,
    }),

    indicatorSeparator: () => ({ display: "none" }),

    option: (base, state) => ({
        ...base,
        backgroundColor: `${
            state.isFocused
                ? colors.neutral2
                : state.isSelected
                ? colors.primary6
                : colors.neutral0
        }`,
        color: `${
            state.isFocused
                ? colors.neutral8
                : state.isSelected
                ? colors.neutral0
                : colors.neutral8
        }`,
        transition: `background-color ${timings.quick}`,
        "&:hover": {
            backgroundColor: `${colors.primary2}`,
        },
    }),

    control: (base, state) => ({
        ...base,
        minHeight: 36, // to override the base style min-height :/
        maxHeight: 36,
        boxShadow: state.isFocused ? `0 0 5px ${colors.primary2}` : 0,
        borderWidth: borders.width,
        borderStyle: "solid",
        borderColor: `${
            state.isDisabled
                ? colors.neutral3
                : state.isFocused
                ? colors.primary7
                : colors.neutral4
        }`,
        backgroundColor: `${
            state.isDisabled ? colors.neutral1 : colors.neutral0
        }`,
        transition: `border ${timings.medium}`,
        borderRadius: `${borders.radius}`,
        "&:hover": {
            borderColor: `${colors.primary7}`,
        },
    }),

    menu: styles => ({
        ...styles,
        boxShadow: "0 8px 16px 0 rgba(24, 19, 87, 0.07)",
        backgroundColor: colors.neutral0,
        borderRadius: `${borders.radius}`,
        border: "0px",
        padding: "0px",
        margin: "0px",
        zIndex: 999,
    }),
};

const errorStyles = {
    container: (base, state) => ({
        ...base,
        borderRadius: `${borders.radius}`,
        outline: 0,
        maxHeight: 36,
        caretColor: `${colors.error5}`,
    }),

    indicatorSeparator: () => ({ display: "none" }),

    option: (base, state) => ({
        ...base,
        backgroundColor: `${
            state.isSelected ? colors.error1 : colors.neutral0
        }`,
        color: `${state.isSelected ? colors.error8 : colors.neutral8}`,
        transition: `background-color ${timings.quick}`,
        "&:hover": {
            backgroundColor: `${colors.error2}`,
        },
    }),
    control: (base, state) => ({
        ...base,
        minHeight: 36, // to override the base style min-height :/
        maxHeight: 36,
        boxShadow: state.isFocused ? `0 0 5px ${colors.error2}` : 0,
        border: `${borders.width} solid ${
            state.isFocused ? colors.error7 : colors.error5
        }`,
        borderRadius: `${borders.radius}`,
        backgroundColor: `${colors.error1}`,
        transition: `border ${timings.medium}`,
        "&:hover": {
            borderColor: `${colors.error7}`,
        },
    }),

    menu: styles => ({
        ...styles,
        boxShadow: "0 8px 16px 0 rgba(24, 19, 87, 0.07)",
        backgroundColor: colors.neutral1,
        borderRadius: `${borders.radius}`,
        border: "0px",
        padding: "0px",
        margin: "0px",
        zIndex: 999,
    }),
};

export const SearchableDropdown = props => (
    <Select {...props} styles={props.error ? errorStyles : customStyles} />
);

export const FormikSearchableDropdown = ({
    field,
    form: { touched, errors, setFieldValue },
    options,
    ...props
}) => (
    <>
        <Select
            id={props.id}
            data-testid={props.id}
            inputId={props["data-testid"]}
            options={options}
            name={field.name}
            value={
                options
                    ? options.find(option => option.value === field.value)
                    : ""
            }
            onChange={option => setFieldValue(field.name, option.value)}
            onBlur={field.onBlur}
            placeholder={null}
            styles={
                touched[field.name] && errors[field.name]
                    ? errorStyles
                    : customStyles
            }
        />
        {touched[field.name] && errors[field.name] && (
            <ValidationError
                data-testid={`${props["data-testid"]}-validation-message`}
                className="error"
            >
                {errors[field.name]}
            </ValidationError>
        )}
    </>
);

const subjectValidator = value => {
    let error;
    if (!value) {
        error = "Please enter a subject.";
    }
    return error;
};

export const FormikSubjectPicker = ({ subjects, index, touched, errors }) => (
    <Field
        validate={subjectValidator}
        validateOnBlur={true}
        validateOnChange={true}
        name={`subjects.${index}.subject`}
        render={({ field, form }) => (
            <FormField
                key={`subjects.${index}.subject`}
                testId={`subjects.${index}.subject-formfield`}
            >
                <Label htmlFor="subject">Subject</Label>
                <SearchableDropdown
                    value={{
                        label: _get(field, "value.label"),
                        value: _get(field, "value.value"),
                    }}
                    options={subjects.map(s => ({
                        key: s.id,
                        value: s.id,
                        label: s.name,
                    }))}
                    error={
                        touched.subjects &&
                        getIn(errors, `subjects.${index}.subject`)
                    }
                    onChange={option => {
                        return form.setFieldValue(field.name, option);
                    }}
                />
                {touched.subjects &&
                    getIn(errors, `subjects.${index}.subject`) && (
                        <ValidationError>
                            {getIn(errors, `subjects.${index}.subject`)}
                        </ValidationError>
                    )}
            </FormField>
        )}
    />
);

const gradeValidator = value => {
    let error;
    if (!value) {
        error = "Please enter a grade.";
    } else if (value.value.length > 100) {
        error = "The grade you have entered is too long.";
    }
    return error;
};

export const FormikGradePicker = ({
    freeText,
    grades,
    index,
    touched,
    errors,
    handleBlur,
}) => (
    <Field
        validate={gradeValidator}
        name={`subjects.${index}.grade`}
        render={({ field, form }) => (
            <FormField
                key={`subjects.${index}.grade`}
                testId={`subjects.${index}.grade-formfield`}
            >
                <Label htmlFor="grade">Grade</Label>
                {freeText ? (
                    <Input
                        type="text"
                        onChange={e => {
                            return form.setFieldValue(field.name, {
                                value: e.target.value,
                                label: e.target.value,
                            });
                        }}
                        onBlur={handleBlur}
                        value={_get(field, "value.value")}
                        name={`subjects.${index}.grade`}
                        error={
                            touched.subjects &&
                            getIn(errors, `subjects.${index}.grade`)
                        }
                    />
                ) : (
                    <SearchableDropdown
                        value={{
                            label: _get(field, "value.label"),
                            value: _get(field, "value.value"),
                        }}
                        options={grades.map(g => ({
                            key: g,
                            value: g,
                            label: g,
                        }))}
                        error={
                            touched.subjects &&
                            getIn(errors, `subjects.${index}.grade`)
                        }
                        onChange={option => {
                            return form.setFieldValue(field.name, option);
                        }}
                    />
                )}

                {touched.subjects &&
                    getIn(errors, `subjects.${index}.grade`) && (
                        <ValidationError>
                            {getIn(errors, `subjects.${index}.grade`)}
                        </ValidationError>
                    )}
            </FormField>
        )}
    />
);

/* 
    GCSEs can have two grades (for double award science) and will be posted to the API
    as an array. As the non-GCSE subjects are not going to be sent as arrays, I've preserved
    the existing picker for them above and made another one for GCSE subjects.
*/
const twoInputContainerStyle = css`
    display: flex;
    flex-direction: row;
    > div {
        flex: 1;
    }
    > div:first-child {
        margin-right: 24px;
    }
`;

const gradeValidatorGCSE = doubleAward => value => {
    let error;
    if (doubleAward) {
        console.log(value);
        if (value.length < 2 || value.includes(undefined)) {
            error = "Please enter two grades.";
        }
    } else if (value.length < 1) {
        error = "Please enter a grade.";
    }
    return error;
};

export const FormikGCSEGradePicker = ({
    grades,
    index,
    touched,
    errors,
    doubleAward,
}) => (
    <Field
        validate={gradeValidatorGCSE(doubleAward)}
        name={`subjects.${index}.grades`}
        render={({ field, form }) => (
            <FormField
                key={`subjects.${index}.grades`}
                testId={`subjects.${index}.grade-formfield`}
            >
                <Label htmlFor="grade">Grade</Label>
                {/* <p>{JSON.stringify(field)}</p> */}
                {doubleAward ? (
                    <div css={twoInputContainerStyle}>
                        <SearchableDropdown
                            value={{
                                label: _get(field, "value.0.label"),
                                value: _get(field, "value.0.value"),
                            }}
                            options={grades.map(g => ({
                                key: g,
                                value: g,
                                label: g,
                            }))}
                            error={
                                touched.subjects &&
                                getIn(errors, `subjects.${index}.grades`)
                            }
                            onChange={option => {
                                const grade1 = _get(field, "value.1");
                                return form.setFieldValue(field.name, [
                                    option,
                                    grade1,
                                ]);
                            }}
                        />
                        <SearchableDropdown
                            value={{
                                label: _get(field, "value.1.label"),
                                value: _get(field, "value.1.value"),
                            }}
                            options={grades.map(g => ({
                                key: g,
                                value: g,
                                label: g,
                            }))}
                            error={
                                touched.subjects &&
                                getIn(errors, `subjects.${index}.grades`)
                            }
                            onChange={option => {
                                const grade0 = _get(field, "value.0");
                                return form.setFieldValue(field.name, [
                                    grade0,
                                    option,
                                ]);
                            }}
                        />
                    </div>
                ) : (
                    <SearchableDropdown
                        value={{
                            label: _get(field, "value.0.label"),
                            value: _get(field, "value.0.value"),
                        }}
                        options={grades.map(g => ({
                            key: g,
                            value: g,
                            label: g,
                        }))}
                        error={
                            touched.subjects &&
                            getIn(errors, `subjects.${index}.grades`)
                        }
                        onChange={option => {
                            return form.setFieldValue(field.name, [option]);
                        }}
                    />
                )}

                {touched.subjects &&
                    getIn(errors, `subjects.${index}.grades`) && (
                        <ValidationError>
                            {getIn(errors, `subjects.${index}.grades`)}
                        </ValidationError>
                    )}
            </FormField>
        )}
    />
);

export default Input;
